import React, { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { GuarContractResponse } from 'services/guaranty';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { Form, Tag } from 'antd';
import { indexBy } from 'utils/utils2';
import ModalController from 'components/ModalController';
import ThirdPartyModal from 'pages/ThirdParty/ThirdPartyDetail/ThirdPartyModal';
import ProposalModal from 'pages/ProposalV3/ProposalModal';
import { toCurrency } from 'utils/utils';
import {
  CheckCircleOutlined
} from '@ant-design/icons';

type Values = {
  guaranty: GuarContractResponse[];
};

const AssociatedContract = (props:{currcode:string,refetch:()=>void,data:any}) => {
  const {currcode,data}=props;
  const [form] = Form.useForm();
  const fieldName = 'guaranty';
  // const { idOrCreate } = useParams<{ idOrCreate: string }>();



  useEffect(() => {
    if (data) {
      form.setFieldsValue({ [fieldName]: data });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);


  const getName = useCallback(
    (name: string) => (record: GuarContractResponse) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'gassocid')[record.gassocid];

      return [fieldName, index, name];
    },
    [form]
  );


  const colorTag = (status: string) => {
    if (status === 'ACTIVE') {
      return 'success'
    } else if (status === 'INIT') {
      return 'default'
    } else if (status === 'DISABLED') {
      return 'red'
    } else if (status === 'TERMINATED') {
      return 'geekblue'
    }
    return ''
  }

  const columns = useMemo<DataGridColDef<GuarContractResponse>[]>(
    () => [
      {
        dataIndex: 'ctreference',
        fixed: 'left',
        width: 120,
        render: (ctreference, record) => {
          return (
            <Form.Item noStyle name={getName('ctreference')(record)}>
              <ModalController>
                {({ setVisible, visible }) => (
                  <>
                    <a
                      onClick={() => {
                        setVisible(true);
                      }}
                    >
                      {record.ctreference??record.propreference}
                    </a>
                    <ProposalModal
                      ctrid={record.ctrid}
                      visible={visible}
                      onCancel={() => setVisible(false)}
                      onSuccess={() => setVisible(false)}
                      title={record.clientname}
                      iscreate={false}
                    />
                  </>
                )}
              </ModalController>{' '}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'clientreference',
        
        render: (clientreference, record) => {
          return (
            <Form.Item noStyle name={getName('clientreference')(record)}>
              <ModalController>
                {({ setVisible, visible }) => (
                  <>
                    <a
                      onClick={() => {
                        setVisible(true);
                      }}
                    >
                      {record.clientreference}
                    </a>
                    <ThirdPartyModal
                      tpId={record.tpidclient}
                      visible={visible}
                      onCancel={() => setVisible(false)}
                      onSuccess={() => setVisible(false)}
                      title={record.clientname}
                      iscreate={false}
                    />
                  </>
                )}
              </ModalController>{' '}
            </Form.Item>
          );
        },
        fixed: 'left',
        width: 120,
      },
      {
        dataIndex: 'clientname',
        
        fixed: 'left',
        width: 120,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('ctreference')(record)}>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'coverageamount',
        
        width: 120,

        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('ctrid')(record)}>
              {toCurrency(value,currcode)}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'startdate',
        
        width: 120,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('startdate')(record)}>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'endate',
        width: 120,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('endate')(record)}>
              {value}
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'statuslabel',
        width: 120,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('statuslabel')(record)}>
              <Tag icon={record.status === 'ACTIVE' && <CheckCircleOutlined />} color={colorTag(record.status)}>
                {value}
              </Tag>
            </Form.Item>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getName]
  );

  return (
      <Form form={form}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const Guaranties: GuarContractResponse[] = getFieldValue(fieldName);
            return (
              <div style={{backgroundColor: '#FFF', paddingTop: '0' }}>
              <DataGrid
                rowKey="guarid"
                dataSource={Guaranties}
                titleComcode="guaranty"
                columns={columns}
              />
              </div>
            );
          }}
        </Form.Item>
      </Form>
  );
};

export default AssociatedContract;
